import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { getConversationMessages } from '../api/messageApi';
import { socketService } from '@/features/messaging/api/socketService';
import {
  Message,
  ConversationMessagesResponse,
  TypingIndicator,
} from '../types';
import { normalizeMessage } from '../utils/normalizeMessage';
import { mergeMessageUpdate } from '../utils/mergeMessageUpdate';

/**
 * Hook for managing conversation messages
 * 
 * Automatically marks messages as read when conversation is opened.
 * Integrates with WebSocket for real-time updates.
 * 
 * Note: Assumes WebSocket is already connected via useWebSocketConnection().
 * 
 * @param conversationId - Conversation ID
 * @param limit - Number of messages to load
 */
export const useConversationMessages = (conversationId: string, limit = 50) => {
  const queryClient = useQueryClient();
  const [typingUsers, setTypingUsers] = useState<Set<string>>(new Set());
  // Maps the typing workspace-owner id → the name of the delegate currently
  // typing on their behalf (Gap 2D). Empty when the owner types directly.
  const [typingPerformers, setTypingPerformers] = useState<Record<string, string>>({});

  const query = useQuery<ConversationMessagesResponse>({
    queryKey: ['conversation-messages', conversationId],
    queryFn: () => getConversationMessages(conversationId, limit),
    enabled: !!conversationId,
    retry: (failureCount, error: any) => {
      // Don't retry on 403/404 for new conversations (no messages yet)
      if (error?.statusCode === 403 || error?.statusCode === 404) {
        return false;
      }
      // Retry other errors up to 3 times
      return failureCount < 3;
    },
  });

  // WebSocket event handlers (connection is managed by useWebSocketConnection)
  useEffect(() => {
    if (!conversationId) return;

    // Wait for socket to be connected before setting up handlers
    const setupHandlers = () => {
      if (!socketService.isConnected()) {
        // Wait a bit and try again
        const timeoutId = setTimeout(setupHandlers, 100);
        return () => clearTimeout(timeoutId);
      }

      // Handle new messages
      const handleNewMessage = (message: Message) => {
        const normalizedMessage = normalizeMessage(message as any);

        if (normalizedMessage.conversationId === conversationId) {
          queryClient.setQueryData<ConversationMessagesResponse>(
            ['conversation-messages', conversationId],
            (old) => {
              // If no existing data (new conversation), refetch to get proper structure
              if (!old) {
                queryClient.invalidateQueries({ 
                  queryKey: ['conversation-messages', conversationId] 
                });
                return old;
              }
              
              // Check if message already exists (prevent duplicates)
              const messageExists = old.messages.some(m => m.id === normalizedMessage.id);
              if (messageExists) {
                return old;
              }
              
              return {
                ...old,
                messages: [...old.messages, normalizedMessage],
              };
            }
          );

          // Mark as read immediately
          socketService.markAsRead(conversationId);
        }

        // Invalidate conversations list to update unread counts
        queryClient.invalidateQueries({ queryKey: ['conversations'] });
      };

      // Handle message updates
      const handleMessageUpdate = (message: Message) => {
        const normalizedMessage = normalizeMessage(message as any);

        if (normalizedMessage.conversationId === conversationId) {
          queryClient.setQueryData<ConversationMessagesResponse>(
            ['conversation-messages', conversationId],
            (old) => {
              if (!old) return old;
              
              // Check if message already exists
              const messageExists = old.messages.some(m => m.id === normalizedMessage.id);
              
              if (messageExists) {
                // Update existing message (merge to avoid wiping fields when server sends partial updates)
                return {
                  ...old,
                  messages: old.messages.map((msg) =>
                    msg.id === normalizedMessage.id
                      ? mergeMessageUpdate(msg, normalizedMessage as any)
                      : msg
                  ),
                };
              } else {
                // If update is partial and we don't have the original message, don't add a blank message bubble.
                if (!normalizedMessage.content) {
                  queryClient.invalidateQueries({ queryKey: ['conversation-messages', conversationId] });
                  return old;
                }

                // Add new message (in case newMessage event was missed)
                return {
                  ...old,
                  messages: [...old.messages, normalizedMessage],
                };
              }
            }
          );
        }
      };

      // Handle typing indicators
      const handleTypingIndicator = (data: TypingIndicator) => {
        const performerName = data.performedBy?.name;

        setTypingUsers(prev => {
          const newSet = new Set(prev);
          if (data.isTyping) {
            newSet.add(data.userId);
          } else {
            newSet.delete(data.userId);
          }
          return newSet;
        });

        setTypingPerformers(prev => {
          const next = { ...prev };
          if (data.isTyping && performerName) {
            next[data.userId] = performerName;
          } else {
            delete next[data.userId];
          }
          return next;
        });

        // Auto-clear typing indicator after 3 seconds
        if (data.isTyping) {
          setTimeout(() => {
            setTypingUsers(prev => {
              const newSet = new Set(prev);
              newSet.delete(data.userId);
              return newSet;
            });
            setTypingPerformers(prev => {
              const next = { ...prev };
              delete next[data.userId];
              return next;
            });
          }, 3000);
        }
      };

      // Register handlers
      socketService.on('newMessage', handleNewMessage);
      socketService.on('messageUpdate', handleMessageUpdate);
      socketService.on('typingIndicator', handleTypingIndicator);

      // Mark conversation as read when opened
      socketService.markAsRead(conversationId);

      // Cleanup
      return () => {
        socketService.off('newMessage', handleNewMessage);
        socketService.off('messageUpdate', handleMessageUpdate);
        socketService.off('typingIndicator', handleTypingIndicator);
      };
    };

    return setupHandlers();
  }, [conversationId, queryClient]);

  return {
    ...query,
    typingUsers: Array.from(typingUsers),
    typingPerformers,
  };
};

